import { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { clearStoredTokens, getStoredToken, storeToken } from '@/lib/authStorage';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);
  const [originalToken, setOriginalToken] = useState(null);

  // ── Renovacion de la sesion por INACTIVIDAD real ─────────────────────────
  // El pase caduca a los MINUTOS_INACTIVIDAD del backend (hoy 40) desde que se
  // emite. Para que eso signifique "inactividad" y no "tiempo desde que entro",
  // la pantalla desliza ese reloj mientras la persona USA el sistema.
  //
  // Antes la actividad se media SOLO por eventos del DOM (mousedown/keydown/
  // scroll/touch), con un throttle atado a [user]. Eso dejaba fuera trabajo
  // real sin esos eventos (revisar una grabacion, estar en llamada mirando la
  // pantalla, leer sin scroll) -> el pase moria a los 40 min ESTANDO
  // trabajando. Ahora: (1) cuenta como actividad CUALQUIER request al backend
  // (lib/api.js emite 'dealider:api-activity') ademas de los eventos del DOM;
  // (2) la renovacion se dispara por RELOJ, no por un evento que podria no
  // ocurrir; (3) usa refs y no depende de [user], para no reiniciarse.
  const ultimaActividadRef = useRef(Date.now());
  const ultimaRenovacionRef = useRef(Date.now());

  // Marca de actividad: TODO lo que hace el usuario cuenta (clic, tecla, scroll,
  // MOVIMIENTO de raton/dedo, cambio de pestana) + cualquier llamada al backend.
  useEffect(() => {
    const marcar = () => { ultimaActividadRef.current = Date.now(); };
    const eventos = ['mousedown', 'mousemove', 'keydown', 'scroll', 'wheel',
                     'touchstart', 'pointerdown', 'click', 'focus'];
    eventos.forEach((e) => window.addEventListener(e, marcar, { passive: true }));
    window.addEventListener('dealider:api-activity', marcar);
    const onVis = () => { if (document.visibilityState === 'visible') marcar(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      eventos.forEach((e) => window.removeEventListener(e, marcar));
      window.removeEventListener('dealider:api-activity', marcar);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  // Renovacion por RELOJ (no por un evento que podria no ocurrir): cada minuto
  // revisa; si hubo actividad desde la ultima renovacion y ya paso el throttle
  // (5 min), renueva el pase -> queda con 40 min NUEVOS. Mientras el usuario
  // use el sistema, la sesion NUNCA se cae. Si deja de usarlo, no se renueva y
  // el pase caduca ~40 min despues de su ultima actividad. Usa refs, no depende
  // de [user] (eso reiniciaba el reloj). skipAuthRedirect: si el refresh diera
  // 401 no expulsa de golpe; el 401 de la siguiente request normal se encarga.
  useEffect(() => {
    const RENOVAR_CADA_MS = 5 * 60 * 1000; // a lo mucho una renovacion cada 5 min
    const REVISAR_CADA_MS = 60 * 1000;

    const id = setInterval(async () => {
      const token = getStoredToken();
      if (!token) return; // sin sesion
      const ahora = Date.now();
      const huboActividad = ultimaActividadRef.current > ultimaRenovacionRef.current;
      const pasoThrottle = ahora - ultimaRenovacionRef.current >= RENOVAR_CADA_MS;
      if (!huboActividad || !pasoThrottle) return; // inactivo o aun no toca
      ultimaRenovacionRef.current = ahora;
      try {
        const r = await api.post('/auth/refresh', null, { skipAuthRedirect: true });
        if (r.data?.token) {
          storeToken(r.data.token);
          api.defaults.headers.common['Authorization'] = `Bearer ${r.data.token}`;
        }
      } catch { /* el pase actual sigue valido hasta que caduque */ }
    }, REVISAR_CADA_MS);

    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const token = getStoredToken();
    const impToken = localStorage.getItem('original_token');
    if (impToken) setOriginalToken(impToken);
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      api.get('/auth/me').then(r => {
        setUser(r.data);
        setImpersonating(!!impToken);
        setLoading(false);
      }).catch(() => { clearStoredTokens(); localStorage.removeItem('original_token'); setLoading(false); });
    } else setLoading(false);
  }, []);

  const login = async (email, password, extraPayload = null) => {
    const payload = extraPayload || { email, password };
    const r = await api.post('/auth/login', payload);
    storeToken(r.data.token);
    api.defaults.headers.common['Authorization'] = `Bearer ${r.data.token}`;
    const me = await api.get('/auth/me');
    setUser(me.data);
    return me.data;
  };

  const register = async (data) => {
    const r = await api.post('/auth/register', data);
    storeToken(r.data.token);
    api.defaults.headers.common['Authorization'] = `Bearer ${r.data.token}`;
    const me = await api.get('/auth/me');
    setUser(me.data);
    return me.data;
  };

  const impersonate = async (companyId) => {
    const currentToken = getStoredToken();
    const r = await api.post(`/admin/impersonate/${companyId}`);
    localStorage.setItem('original_token', currentToken);
    storeToken(r.data.token);
    api.defaults.headers.common['Authorization'] = `Bearer ${r.data.token}`;
    const me = await api.get('/auth/me');
    setUser(me.data);
    setImpersonating(true);
    setOriginalToken(currentToken);
    return me.data;
  };

  const stopImpersonation = async () => {
    const orig = localStorage.getItem('original_token');
    if (orig) {
      storeToken(orig);
      localStorage.removeItem('original_token');
      api.defaults.headers.common['Authorization'] = `Bearer ${orig}`;
      const me = await api.get('/auth/me');
      setUser(me.data);
      setImpersonating(false);
      setOriginalToken(null);
      return me.data;
    }
    return null;
  };

  const logout = () => {
    clearStoredTokens();
    localStorage.removeItem('original_token');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setImpersonating(false);
    setOriginalToken(null);
  };

  const refreshUser = async () => {
    try {
      const me = await api.get('/auth/me');
      setUser(me.data);
    } catch {}
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, impersonate, stopImpersonation, impersonating, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
